import {
Component,
Inject,
InjectionToken,
Input,
OnDestroy,
OnInit
} from '@angular/core';
import { FormControl,FormGroup,ReactiveFormsModule } from '@angular/forms';
import { MatDialog,MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { combineLatest,merge,of,Subject } from 'rxjs';
import {
catchError,
debounceTime,
distinctUntilChanged,
map,
startWith,
switchMap,
takeUntil,
} from 'rxjs/operators';

import {
MatButton,
MatButtonModule,
MatIconButton,
} from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { ConfirmDialog } from '../../dialogs/confirm.dialog';
import {
ErrorServiceToken,
LogServiceToken,
RepositoryServiceToken
} from '../../services';
import {
IErrorService,
ILogService,
IRepositoryService,
} from '../../services/i';
import { RoleViewModel,UserViewModel } from '../../view-models';
import {
UserFilterType,
UserFilterViewModel
} from '../../view-models/filters';
import { HeaderComponent } from '../header/header.component';

@Component({
  selector: 'app-user-manager',
  templateUrl: './user-manager.component.html',
  styleUrls: ['./user-manager.component.css'],
  imports: [
    HeaderComponent,
    MatButton,
    MatToolbarModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatIcon,
    MatSelectModule,
    MatTableModule,
    MatProgressSpinnerModule,
    RouterLink,
    MatIconButton,
    MatInputModule,
    MatButtonModule,
  ],
  standalone: true,
})
export class UserManagerComponent implements OnInit, OnDestroy {
  dialogRef: MatDialogRef<ConfirmDialog>;
  users: UserViewModel[] = [];
  roles: RoleViewModel[] = [];
  displayedColumns = ['name', 'identity', 'roles', 'actions'];
  loading = true;
  rolesLoading = true;
  loadError = '';
  roleLoadError = '';
  @Input() keywords: FormControl = new FormControl('');
  @Input() userRole: FormControl = new FormControl(null);
  @Input() form: FormGroup = new FormGroup({
    keywords: this.keywords,
    role: this.userRole,
  });
  private readonly destroy$ = new Subject<void>();
  private readonly refreshResults$ = new Subject<void>();
  private requestId = 0;

  // http://blog.angular-university.io/introduction-to-angular-2-forms-template-driven-vs-model-driven/
  // Regarding subscribe and emit: https://scotch.io/tutorials/angular-2-http-requests-with-observables
  // Automapper (if really necessary): https://github.com/loedeman/AutoMapper/wiki
  constructor(
    @Inject(RepositoryServiceToken) private repo: IRepositoryService,
    @Inject(LogServiceToken) private log: ILogService,
    @Inject(ErrorServiceToken) private errors: IErrorService,
    public dialog: MatDialog
  ) {}

  updateResults() {
    this.refreshResults$.next();
  }

  clearFilters() {
    this.keywords.setValue('', { emitEvent: false });
    this.userRole.setValue(null, { emitEvent: false });
    this.updateResults();
  }

  get hasActiveFilters(): boolean {
    return (
      !!String(this.keywords.value || '').trim() ||
      (this.userRole.value !== null && typeof this.userRole.value !== 'undefined')
    );
  }

  visibleRoles(user: UserViewModel): string[] {
    return (user.UserRoles || [])
      .map(userRole => userRole.Role?.RoleName)
      .filter(roleName => !!roleName && roleName !== 'ValidUser');
  }

  displayHawkId(user: UserViewModel): string {
    const storedValue = String(user.HawkId || '').trim();
    if (storedValue && !/[\s,]/.test(storedValue)) {
      return storedValue.includes('@')
        ? storedValue.slice(0, storedValue.indexOf('@'))
        : storedValue;
    }

    const email = String(user.Email || '').trim();
    const atIndex = email.indexOf('@');
    return atIndex > 0 ? email.slice(0, atIndex) : '';
  }

  openDialog(message: string): Promise<any> {
    this.dialogRef = this.dialog.open(ConfirmDialog, {
      disableClose: false,
    });
    this.dialogRef.componentInstance.title = 'Confirm Removal';
    this.dialogRef.componentInstance.message = message;

    this.dialogRef.afterClosed().subscribe(result => {
      this.dialogRef = null;
    });
    return this.dialogRef.afterClosed().toPromise();
  }

  removeUser(user: UserViewModel) {
    this.openDialog(`Are you sure you want to remove ${user.FullName}?`).then(
      confirm => {
        if (confirm) {
          this.repo
            .Delete(UserViewModel, user.Id)
            .toPromise()
            .then(r => {
              this.updateResults();
            })
            .catch(e => {
              this.errors.broadcast(
                'Error',
                UserManagerComponentToken,
                `Unabled to remove user ${user.FullName}.`
              );
              this.log.warn('Error: ', e);
            });
        }
      }
    );
  }

  ngOnInit() {
    const filterChanges$ = combineLatest([
      this.keywords.valueChanges.pipe(
        startWith(this.keywords.value || ''),
        debounceTime(400),
        distinctUntilChanged()
      ),
      this.userRole.valueChanges.pipe(
        startWith(this.userRole.value),
        distinctUntilChanged()
      ),
    ]).pipe(
      map(([keywords, roleId]) => ({
        keywords: String(keywords || ''),
        roleId:
          roleId === null || typeof roleId === 'undefined'
            ? null
            : Number(roleId),
      }))
    );
    const manualRefresh$ = this.refreshResults$.pipe(
      map(() => ({
        keywords: String(this.keywords.value || ''),
        roleId:
          this.userRole.value === null ||
          typeof this.userRole.value === 'undefined'
            ? null
            : Number(this.userRole.value),
      }))
    );

    merge(filterChanges$, manualRefresh$)
      .pipe(
        switchMap(criteria => {
          const requestId = ++this.requestId;
          const filter = new UserFilterViewModel();
          const trimmedKeywords = criteria.keywords.trim();
          filter.Keywords = trimmedKeywords ? trimmedKeywords.split(/\s+/) : [];
          filter.Type = UserFilterType.RoleAndKeywords;
          filter.RoleId = criteria.roleId;
          this.loading = true;
          this.loadError = '';

          return this.repo.Page(UserViewModel, filter).pipe(
            map(users => ({ requestId, users, error: null })),
            catchError(error =>
              of({ requestId, users: null, error })
            )
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(result => {
        if (result.requestId !== this.requestId) {
          return;
        }
        this.loading = false;
        if (result.error) {
          this.loadError = 'Unable to load users. Please try again.';
          this.log.warn('Unable to load users.', result.error);
          return;
        }
        this.users = (result.users || []).sort((a, b) =>
          a.Last === b.Last
            ? (a.First || '').localeCompare(b.First || '')
            : (a.Last || '').localeCompare(b.Last || '')
        );
      });

    const roleFilter = new UserFilterViewModel();
    roleFilter.PageSize = 50;
    this.repo.Page(RoleViewModel, roleFilter).subscribe({
      next: roles => {
        this.roles = roles;
        this.rolesLoading = false;
      },
      error: error => {
        this.rolesLoading = false;
        this.roleLoadError = 'Role filters are temporarily unavailable.';
        this.log.warn('Unable to load roles.', error);
      },
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

export const UserManagerComponentToken = new InjectionToken(
  'UserManagerComponent'
);
